// src/pages/ResourceDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resourcesAPI, companiesAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Edit2, Plus, Trash2, Mail, Phone, MapPin,
  Linkedin, Briefcase, FileText, Clock, CheckCircle, Upload,
} from 'lucide-react';
import { PhoneLink } from '../utils/helpers';
import TracabilityInfo from '../components/common/TracabilityInfo';
import CVImportBadge from '../components/common/CVImportBadge';


const TYPE_COLOR = { CDI:'#FF4100', INTERCO:'#F59E0B', STAGIAIRE:'#A78BFA', ALTERNANT:'#06B6D4', COMMERCIAL:'#EA580C', EXTERNE:'var(--text-muted)', FREELANCE:'#16A34A' };
const STATUS_COLOR = { ACTIF:'#16A34A', EN_MISSION:'#FF4100', FUTURE_ARRIVEE:'#A78BFA', INTERNE:'#06B6D4', EXTERNE:'#EA580C', INTERCONTRAT:'#F59E0B', CONGE:'#94A3B8', INACTIF:'var(--text-muted)', SORTI:'#DC2626' };
const MISSION_STATUS_META = {
  EN_COURS: { color:'#16A34A', bg:'rgba(34,197,94,0.08)' },
  FUTURE:   { color:'#FF4100', bg:'var(--orange-50)' },
  TERMINEE: { color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' },
  SUSPENDUE:{ color:'#F59E0B', bg:'#FFFBEB' },
};


export default function ResourceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resource, setResource]   = useState(null);
  const [missions, setMissions]   = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showMissionForm, setShowMissionForm]   = useState(false);
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [newMission, setNewMission]   = useState({ mission_title:'', client_name:'', status:'EN_COURS', start_date:'', end_date:'', location:'', internal_comment:'' });
  const [newDoc, setNewDoc]           = useState({ title:'', document_type:'CV', file_url:'' });
  const [uploading, setUploading]     = useState(false);
  const [companies, setCompanies]     = useState([]);


  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rRes, mRes, dRes, cRes] = await Promise.all([
        resourcesAPI.getOne(id),
        resourcesAPI.listMissions(id),
        resourcesAPI.listDocuments(id),
        companiesAPI.list({}),
      ]);
      setResource(rRes.data);
      setMissions(mRes.data || []);
      setDocuments(dRes.data || []);
      setCompanies(cRes.data || []);
    } catch { toast.error('Ressource introuvable'); navigate('/resources'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, [id]);


  const handleAddMission = async () => {
    if (!newMission.mission_title) { toast.error('Titre obligatoire'); return; }
    try {
      await resourcesAPI.createMission(id, { ...newMission, resource_id: id });
      setNewMission({ mission_title:'', client_name:'', status:'EN_COURS', start_date:'', end_date:'', location:'', internal_comment:'' });
      setShowMissionForm(false);
      toast.success('Mission ajoutée'); fetchAll();
    } catch { toast.error('Erreur'); }
  };


  const handleUploadCV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name);
      fd.append('document_type', 'CV');
      await resourcesAPI.uploadDocument(id, fd);
      toast.success('Document uploadé'); fetchAll();
    } catch { toast.error('Erreur upload'); }
    finally { setUploading(false); }
  };


  const handleDeleteMission = async (mid) => {
    if (!window.confirm('Supprimer cette mission ?')) return;
    try { await resourcesAPI.deleteMission(mid); fetchAll(); toast.success('Supprimé'); }
    catch { toast.error('Erreur'); }
  };
  const handleDeleteDoc = async (did) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try { await resourcesAPI.deleteDocument(did); fetchAll(); toast.success('Supprimé'); }
    catch { toast.error('Erreur'); }
  };


  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:'48px', gap:10, color:'var(--text-muted)' }}><div className="spinner"/>Chargement…</div>;
  if (!resource) return null;


  const tc = TYPE_COLOR[resource.employee_type]   || 'var(--text-muted)';
  const sc = STATUS_COLOR[resource.status]         || 'var(--text-muted)';


  const tabs = [
    { key:'overview',  label:'Profil' },
    { key:'missions',  label:`Missions (${missions.length})` },
    { key:'documents', label:`Documents (${documents.length})` },
  ];


  return (
    <div>
      <div className="page-header">
        <div className="page-header-left" style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/resources')}><ArrowLeft size={18}/></button>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div className="avatar avatar-lg" style={{ width:48, height:48, fontSize:'1.125rem' }}>
              {(resource.full_name||'?')[0]}
            </div>
            <div>
              <h1 style={{ margin:0, display:'flex', alignItems:'center', gap:10 }}>
                {resource.full_name}
                <span style={{ padding:'2px 10px', borderRadius:20, fontSize:'0.75rem', fontWeight:700, background:`${tc}18`, color:tc }}>{resource.employee_type}</span>
                <span style={{ padding:'2px 10px', borderRadius:20, fontSize:'0.75rem', fontWeight:700, background:`${sc}18`, color:sc }}>{resource.status}</span>
              </h1>
              <p style={{ margin:0 }}>{resource.title || '—'}</p>
            </div>
          </div>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => navigate(`/resources/${id}/edit`)}><Edit2 size={14}/> Modifier</button>
        </div>
      </div>

      {/* Traçabilité */}
      <div style={{ marginBottom: 16 }}>
        <TracabilityInfo
          createdAt={resource.created_at}
          createdById={resource.owner_user_id}   /* fallback : owner sert de "créé par" */
          updatedAt={resource.updated_at}
        />
      </div>


      {/* Tabs */}
      <div style={{ display:'flex', gap:4, borderBottom:'2px solid var(--border)', marginBottom:20 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{ padding:'8px 16px', border:'none', cursor:'pointer', fontWeight:600, fontSize:'0.875rem',
              background:'none', borderBottom: activeTab === t.key ? '2px solid #FF4100' : '2px solid transparent',
              color: activeTab === t.key ? '#FF4100' : 'var(--text-muted)', marginBottom:-2 }}>
            {t.label}
          </button>
        ))}
      </div>


      {/* Profil */}
      {activeTab === 'overview' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Coordonnées</span></div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {resource.email && <InfoRow icon={<Mail size={14}/>} label="Email" value={<a href={`mailto:${resource.email}`} style={{ color:'#FF4100' }}>{resource.email}</a>}/>}
              {resource.email_2 && <InfoRow icon={<Mail size={14}/>} label="Email 2" value={resource.email_2}/>}
              {resource.email_3 && <InfoRow icon={<Mail size={14}/>} label="Email 3" value={resource.email_3}/>}
              {resource.phone && <InfoRow icon={<Phone size={14}/>} label="Téléphone" value={<PhoneLink phone={resource.phone} style={{ color:'#FF4100' }}/>}/>}
              {resource.phone_2 && <InfoRow icon={<Phone size={14}/>} label="Téléphone 2" value={<PhoneLink phone={resource.phone_2} style={{ color:'#FF4100' }}/>}/>}
              {resource.phone_3 && <InfoRow icon={<Phone size={14}/>} label="Téléphone 3" value={<PhoneLink phone={resource.phone_3} style={{ color:'#FF4100' }}/>}/>}
              {(resource.address || resource.city) && <InfoRow icon={<MapPin size={14}/>} label="Adresse" value={[resource.address, resource.postal_code, resource.city, resource.country].filter(Boolean).join(', ')}/>}
              {resource.location && !resource.city && <InfoRow icon={<MapPin size={14}/>} label="Localisation" value={resource.location}/>}
              {resource.birth_date && <InfoRow icon={<Clock size={14}/>} label="Date de naissance" value={resource.birth_date}/>}
              {resource.nationality && <InfoRow icon={<Briefcase size={14}/>} label="Nationalité" value={resource.nationality}/>}
              {resource.family_situation && <InfoRow icon={<Briefcase size={14}/>} label="Situation familiale" value={resource.family_situation}/>}
              {resource.matricule && <InfoRow icon={<Briefcase size={14}/>} label="Matricule" value={resource.matricule}/>}
              {resource.linkedin_link && <InfoRow icon={<Linkedin size={14}/>} label="LinkedIn" value={<a href={resource.linkedin_link} target="_blank" rel="noreferrer" style={{ color:'#FF4100' }}>Voir profil</a>}/>}
              {resource.manager_name && <InfoRow icon={<Briefcase size={14}/>} label="Manager" value={resource.manager_name}/>}
              {resource.recruiter_name && <InfoRow icon={<Briefcase size={14}/>} label="Recruteur RH" value={resource.recruiter_name}/>}
              {resource.pole && <InfoRow icon={<Briefcase size={14}/>} label="Pôle" value={resource.pole}/>}
              {resource.agency && <InfoRow icon={<MapPin size={14}/>} label="Agence" value={resource.agency}/>}
              {resource.boond_ref && <InfoRow icon={<Briefcase size={14}/>} label="Réf. Boond" value={resource.boond_ref}/>}
            </div>
          </div>
          <div className="card">
            <div className="card-header"><span className="card-title">Disponibilité & Contrat</span></div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {resource.availability && <InfoRow icon={<Clock size={14}/>} label="Disponibilité" value={resource.availability}/>}
              {resource.experience_years && <InfoRow icon={<Clock size={14}/>} label="Expérience" value={resource.experience_years}/>}
              {resource.tjm && <InfoRow icon={<Briefcase size={14}/>} label="TJM" value={<span style={{ fontWeight:600, color:'#EA580C' }}>{resource.tjm}</span>}/>}
              {resource.work_mode && <InfoRow icon={<Briefcase size={14}/>} label="Mode travail" value={resource.work_mode}/>}
              {resource.mobility && <InfoRow icon={<MapPin size={14}/>} label="Mobilité" value={resource.mobility}/>}
            </div>
          </div>
          {resource.current_client && (
            <div className="card" style={{ borderLeft:'3px solid #FF4100' }}>
              <div className="card-header"><span className="card-title">Mission en cours</span></div>
              <div style={{ fontSize:'0.875rem' }}>
                <div style={{ fontWeight:700, fontSize:'1rem', marginBottom:4 }}>{resource.current_client}</div>
                {resource.current_mission && <div style={{ color:'var(--text-muted)', marginBottom:6 }}>{resource.current_mission}</div>}
                <div style={{ display:'flex', gap:12, fontSize:'0.8125rem', color:'var(--text-muted)' }}>
                  {resource.mission_start_date && <span>Début: {resource.mission_start_date}</span>}
                  {resource.mission_end_date && <span>Fin: {resource.mission_end_date}</span>}
                </div>
              </div>
            </div>
          )}
          {resource.summary && (
            <div className="card">
              <div className="card-header"><span className="card-title">Résumé profil</span></div>
              <p style={{ fontSize:'0.875rem', margin:0, lineHeight:1.6 }}>{resource.summary}</p>
            </div>
          )}
        </div>
      )}


      {/* Missions */}
      {activeTab === 'missions' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Historique missions</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowMissionForm(s => !s)}><Plus size={13}/> Ajouter</button>
          </div>
          {showMissionForm && (
            <div style={{ padding:'12px 16px', background:'var(--bg-elevated)', borderRadius:8, margin:'0 0 12px', border:'1px solid var(--border)' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                <div><label style={lbl}>Titre mission *</label><input style={inp} value={newMission.mission_title} onChange={e => setNewMission(f => ({...f, mission_title:e.target.value}))}/></div>
                <div>
                  <label style={lbl}>Client</label>
                  <select style={inp} value={newMission.client_name} onChange={e => setNewMission(f => ({...f, client_name:e.target.value}))}>
                    <option value="">-- Choisir ou saisir --</option>
                    {companies.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                  <input style={{ ...inp, marginTop:5 }} placeholder="Ou saisir manuellement…" value={newMission.client_name} onChange={e => setNewMission(f => ({...f, client_name:e.target.value}))}/>
                </div>
                <div><label style={lbl}>Début</label><input type="date" style={inp} value={newMission.start_date} onChange={e => setNewMission(f => ({...f, start_date:e.target.value}))}/></div>
                <div><label style={lbl}>Fin</label><input type="date" style={inp} value={newMission.end_date} onChange={e => setNewMission(f => ({...f, end_date:e.target.value}))}/></div>
                <div><label style={lbl}>Localisation</label><input style={inp} value={newMission.location} onChange={e => setNewMission(f => ({...f, location:e.target.value}))}/></div>
                <div><label style={lbl}>Statut</label>
                  <select style={inp} value={newMission.status} onChange={e => setNewMission(f => ({...f, status:e.target.value}))}>
                    <option value="EN_COURS">En cours</option>
                    <option value="FUTURE">Future</option>
                    <option value="TERMINEE">Terminée</option>
                    <option value="SUSPENDUE">Suspendue</option>
                  </select>
                </div>
              </div>
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={lbl}>Note</label>
                  <textarea style={{ ...inp, minHeight:50, resize:'vertical' }} value={newMission.internal_comment} onChange={e => setNewMission(f => ({...f, internal_comment:e.target.value}))}/>
                </div>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowMissionForm(false)}>Annuler</button>
                <button className="btn btn-primary btn-sm" onClick={handleAddMission}><CheckCircle size={13}/> Enregistrer</button>
              </div>
            </div>
          )}
          {missions.length === 0 ? (
            <div style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)', fontSize:'0.875rem' }}>Aucune mission enregistrée</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {missions.map(m => (
                <div key={m.id} style={{ padding:'12px 14px', border:'1px solid var(--border)', borderRadius:8, display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                      <span style={{ fontWeight:600, fontSize:'0.875rem' }}>{m.mission_title}</span>
                      {(() => {
                        const ms = MISSION_STATUS_META[m.status] || { color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' };
                        const labels = { EN_COURS:'En cours', FUTURE:'Future', TERMINEE:'Terminée', SUSPENDUE:'Suspendue' };
                        return <span style={{ padding:'1px 8px', borderRadius:20, fontSize:'0.6875rem', fontWeight:600, color:ms.color, background:ms.bg }}>{labels[m.status] || m.status}</span>;
                      })()}
                    </div>
                    <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>
                      {[m.client_name, m.location, m.start_date && m.end_date ? `${m.start_date} → ${m.end_date}` : m.start_date].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-icon btn-sm" style={{ color:'#DC2626' }} onClick={() => handleDeleteMission(m.id)}><Trash2 size={13}/></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Documents */}
      {activeTab === 'documents' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Documents ({documents.length})</span>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <select style={{ ...inp, width:'auto', padding:'5px 8px', fontSize:'0.75rem' }}
                value={newDoc.document_type} onChange={e => setNewDoc(f => ({...f, document_type:e.target.value}))}>
                {['CV','CONTRAT','EVALUATION','AUTRE'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <label style={{ cursor:'pointer' }} className="btn btn-ghost btn-sm">
                {uploading ? <><div className="spinner" style={{ width:12, height:12 }}/> Upload…</> : <><Upload size={13}/> Uploader</>}
                <input type="file" style={{ display:'none' }} accept=".pdf,.doc,.docx" onChange={handleUploadCV}/>
              </label>
            </div>
          </div>
          {documents.length === 0 ? (
            <div style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)', fontSize:'0.875rem' }}>Aucun document</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {documents.map(doc => (
                <div key={doc.id} style={{ padding:'12px 14px', border:'1px solid var(--border)', borderRadius:8, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                  <FileText size={20} color="#64748B"/>
                  <div style={{ flex:1, minWidth:180 }}>
                    <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{doc.title || doc.file_name}</div>
                    <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginBottom:6 }}>{doc.document_type}</div>
                    {/* Badge import : ne s'affiche que pour les CV */}
                    {doc.document_type === 'CV' && (
                      <CVImportBadge
                        importedAt={doc.created_at || doc.uploaded_at}
                        importedById={doc.uploaded_by_user_id || resource.owner_user_id}
                      />
                    )}
                  </div>
                  {doc.file_url && <a href={doc.file_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">Télécharger</a>}
                  <button className="btn btn-ghost btn-icon btn-sm" style={{ color:'#DC2626' }} onClick={() => handleDeleteDoc(doc.id)}><Trash2 size={13}/></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function InfoRow({ icon, label, value }) {
  return (
    <div style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
      <span style={{ color:'var(--text-muted)', flexShrink:0, marginTop:2 }}>{icon}</span>
      <div>
        <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginBottom:1 }}>{label}</div>
        <div style={{ fontSize:'0.875rem' }}>{value}</div>
      </div>
    </div>
  );
}
const lbl = { display:'block', fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.04em' };
const inp = { width:'100%', padding:'8px 10px', border:'1.5px solid var(--border)', borderRadius:7, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' };
// src/pages/ResourceFormPage.jsx